<template>
    <main>
        <h1> On-demand education </h1>
        <BaseSection>
            <BaseFilterEvent
                v-model="filters"
                category-type="onDemandTypes"
                :show-location="false"
                :show-topic="featureFlag('topic-filter')"
                @input="handleInput"
            />
        </BaseSection>

        <BaseSection>
            <ClientOnly>
                <BaseCardCollection
                    v-if="pending || (cards && cards.length)"
                    :cards="cards || []"
                    :wide="true"
                    :placeholder="pending"
                />
                <p v-else>Sorry, there are no items to show here right now.</p>
            </ClientOnly>
        </BaseSection>

        <BaseSection>
            <BasePaginator
                :current-page="currentPage"
                :total-pages="Math.ceil(total / LIMIT)"
                @update:current-page="currentPage = $event"
            />
        </BaseSection>
    </main>
</template>

<script setup>
import OnDemandQuery from '~/graphql/queries/events/OnDemand.graphql';
import BasePaginator from '~/components/BasePaginator/BasePaginator.vue';


const LIMIT = 16;

const { activeSite, featureFlag } = useMultisite();
const currentPage = useCurrentPage();

// eslint-disable-next-line vue/require-prop-types
const filters = defineModel();

const route = useRoute();

filters.value = {
    topic: route.query.topic,
    type: route.query.type,
    location: route.query.location
};

const { variables } = useCraftVariables();
delete variables.uri;

variables.limit = LIMIT;

watchEffect(() => {
    if (filters.value.topic) {
        variables.relatedToEntries = [{ section: 'topics', slug: filters.value.topic }];
    } else {
        delete variables.relatedToEntries;
    }
    if (filters.value.type) {
        variables.relatedToCategories = [{ group: 'onDemandTypes', slug: filters.value.type }];
    } else {
        delete variables.relatedToCategories;
    }

    if (filters.value.location?.length) {
        // Hardcode is or is not city "online"
        variables.city = filters.value.location === 'online' ? 'Online' : ['not', 'Online'];
    } else {
        delete variables.city;
    }

    if (filters.value.topic === null && variables.relatedToEntries) {
        delete variables.relatedToEntries;
    }
    if (filters.value.type === null && variables.relatedToCategories) {
        delete variables.relatedToCategories;
    }
});

const handleInput = (newValue) => {
    filters.value = { ...newValue };
};

watchEffect(() => {
    variables.offset = route?.query?.page ? (route.query.page - 1) * variables.limit : 0; 

    // Watch topic
    if (route.query.topic) {
        variables.relatedToEntries = [{ section: 'topics', slug: route.query.topic }];
    }

    // Watch type
    if (route.query.type) {
        variables.relatedToCategories = [{ group: 'onDemandTypes', slug: route.query.type }];
    }

    // Watch location
    if (route.query.location) {
        variables.city = route.query.location === 'online' ? 'Online' : ['not', 'Online'];
    }
});

const response = await useLazyAsyncQuery({
    query: OnDemandQuery,
    variables,
    watch: filters
});


const title = computed(() => response?.data?.value?.overview?.seoOverviewEducationTitle ?? 'On-demand education');
const description = computed(() => response?.data?.value?.overview?.seoOverviewEducationDescription || activeSite.value?.seo?.description || '');

const {
    craftEntry: entries,
    pending,
} = usePageQuery(
    response,
    {
        entryKey: 'entries',
        seoTitle: title.value,
        seoDescription: description.value,
        activeSite
    }
);

const { makeMeta, makeLocation } = useEventsCard();
const { getCardPills } = usePillsCard();

const cards = computed(() => {
    if (entries.value?.length) {
        return entries.value.map((event) => {
            let image = null;
            if (event.image.length) {
                image = {
                    src: event.image?.[0]?.large,
                    srcset: `${event.image?.[0]?.large} 600w, ${event.image?.[0]?.medium} 407w, ${event.image?.[0]?.small} 327w`
                };
            }
            return {
                image,
                title: event.title,
                meta: makeMeta(event),
                location: makeLocation(event),
                uri: event.uri,
                pills: getCardPills(event),
            };
        });
    }
    return null;
});

const total = computed(() => {
    return response?.data?.value?.entryCount;
});

watch(() => currentPage.value, () => {
    window.scrollTo(0, 0);
});
</script>
